import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Layers, Plus, Pencil, Archive, ExternalLink, Download, Printer } from 'lucide-react';
import { toast } from 'sonner';

export default function GapLibrary() {
  const { hasGapBestPractices } = useAuth();

  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bestPracticesUrl, setBestPracticesUrl] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingGap, setEditingGap] = useState(null); // null → create mode
  const [form, setForm] = useState({ name: '', category: '', description: '' });
  const [saving, setSaving] = useState(false);

  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Pull gaps with their tag counts + tenant name, then build and download
  // a CSV named "<TenantName>_Gap_Library_<YYYY-MM-DD>.csv".
  const handleExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await fetch(`${API}/gaps/export-data`, { credentials: 'include' });
      if (!res.ok) throw new Error('load-failed');
      const data = await res.json();
      const list = Array.isArray(data?.gaps) ? data.gaps : [];

      const escape = (v) => {
        const s = v == null ? '' : String(v);
        if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };

      const lines = ['Gap Name,Category,Description,Times Tagged'];
      for (const g of list) {
        lines.push([
          escape(g.name || ''),
          escape(g.category || ''),
          escape(g.description || ''),
          escape(g.times_tagged ?? 0),
        ].join(','));
      }
      const csv = '\ufeff' + lines.join('\r\n');

      const tenantName = (data?.tenant_name || 'Tenant').replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '') || 'Tenant';
      const today = new Date().toISOString().slice(0, 10);
      const filename = `${tenantName}_Gap_Library_${today}.csv`;

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export gap library');
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    // Print view opens in a new tab; it auto-triggers the print dialog.
    window.open('/gaps/print', '_blank', 'noopener');
  };

  useEffect(() => {
    fetchGaps();
    if (hasGapBestPractices) {
      fetchBestPracticesUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGapBestPractices]);

  const fetchGaps = async () => {
    try {
      const res = await fetch(`${API}/gaps`, { credentials: 'include' });
      if (!res.ok) throw new Error('load-failed');
      const data = await res.json();
      setGaps(Array.isArray(data?.gaps) ? data.gaps : []);
    } catch {
      toast.error('Failed to load gap library');
    } finally {
      setLoading(false);
    }
  };

  const fetchBestPracticesUrl = async () => {
    try {
      const res = await fetch(`${API}/gaps/best-practices-url`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.url) setBestPracticesUrl(data.url);
    } catch {
      /* silent — link is optional */
    }
  };

  const openCreate = () => {
    setEditingGap(null);
    setForm({ name: '', category: '', description: '' });
    setModalOpen(true);
  };

  const openEdit = (gap) => {
    setEditingGap(gap);
    setForm({
      name: gap.name || '',
      category: gap.category || '',
      description: gap.description || '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingGap(null);
  };

  const saveGap = async () => {
    const name = form.name.trim();
    if (!name) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        name,
        category: form.category.trim() || null,
        description: form.description.trim() || null,
      };
      const url = editingGap ? `${API}/gaps/${editingGap.gap_id}` : `${API}/gaps`;
      const method = editingGap ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save gap');
        return;
      }
      toast.success(editingGap ? 'Gap updated' : 'Gap added');
      setModalOpen(false);
      setEditingGap(null);
      fetchGaps();
    } catch {
      toast.error('Failed to save gap');
    } finally {
      setSaving(false);
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      const res = await fetch(`${API}/gaps/${archiveTarget.gap_id}/archive`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to archive gap');
        return;
      }
      toast.success('Gap archived');
      setArchiveTarget(null);
      setGaps(prev => prev.filter(g => g.gap_id !== archiveTarget.gap_id));
    } catch {
      toast.error('Failed to archive gap');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="gap-library-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-['Montserrat']"
              data-testid="gap-library-title"
            >
              Gap Library
            </h1>
            <p className="text-[#64748B] mt-1 text-sm">
              Manage the gaps you can tag customer feedback against.
            </p>
            {hasGapBestPractices && bestPracticesUrl && (
              <a
                href={bestPracticesUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-[#1A3E6F] hover:underline mt-2"
                data-testid="gap-best-practices-link"
              >
                Need help? View best practices
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleExportCsv}
              disabled={exporting || gaps.length === 0}
              variant="outline"
              className="border-[#E2E8F0] text-[#0F172A] hover:bg-[#F8FAFC]"
              data-testid="export-gaps-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <Button
              onClick={handlePrint}
              disabled={gaps.length === 0}
              variant="outline"
              className="border-[#E2E8F0] text-[#0F172A] hover:bg-[#F8FAFC]"
              data-testid="export-gaps-print-btn"
            >
              <Printer className="w-4 h-4 mr-2" /> Print / Export PDF
            </Button>
            <Button
              onClick={openCreate}
              className="bg-[#1A3E6F] hover:bg-[#15325A] text-white"
              data-testid="add-gap-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Gap
            </Button>
          </div>
        </div>

        {loading ? (
          <Card>
            <CardContent className="p-8 text-center text-[#64748B]">
              <div className="loading-spinner mx-auto mb-4" />
              Loading gaps...
            </CardContent>
          </Card>
        ) : gaps.length === 0 ? (
          <Card data-testid="gap-library-empty">
            <CardContent className="p-12 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#1A3E6F]/10 flex items-center justify-center">
                <Layers className="w-6 h-6 text-[#1A3E6F]" />
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A] font-['Montserrat']">
                No gaps yet
              </h3>
              <p className="text-[#64748B] mt-1 text-sm">
                Add your first gap to start tagging responses.
              </p>
              <Button
                onClick={openCreate}
                className="mt-5 bg-[#1A3E6F] hover:bg-[#15325A] text-white"
                data-testid="add-gap-empty-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Gap
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="gap-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-[#64748B] border-b border-[#E2E8F0] bg-[#F8FAFC]">
                      <th className="px-5 py-3 font-semibold">Name</th>
                      <th className="px-5 py-3 font-semibold">Category</th>
                      <th className="px-5 py-3 font-semibold">Description</th>
                      <th className="px-5 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gaps.map(g => (
                      <tr
                        key={g.gap_id}
                        className="border-b border-[#F1F5F9] last:border-b-0 hover:bg-[#F8FAFC]/60"
                        data-testid={`gap-row-${g.gap_id}`}
                      >
                        <td className="px-5 py-3 text-[#0F172A] font-medium">{g.name}</td>
                        <td className="px-5 py-3 text-[#64748B]">
                          {g.category ? (
                            <span className="inline-flex items-center rounded-full bg-[#1A3E6F]/8 text-[#1A3E6F] text-xs px-2.5 py-1">
                              {g.category}
                            </span>
                          ) : (
                            <span className="text-[#CBD5E1]">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-[#64748B] max-w-[420px]">
                          {g.description ? (
                            <span className="line-clamp-2">{g.description}</span>
                          ) : (
                            <span className="text-[#CBD5E1]">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(g)}
                              data-testid={`edit-gap-${g.gap_id}`}
                              className="text-[#1A3E6F] hover:bg-[#1A3E6F]/5"
                            >
                              <Pencil className="w-4 h-4 mr-1" /> Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setArchiveTarget(g)}
                              data-testid={`archive-gap-${g.gap_id}`}
                              className="text-[#64748B] hover:bg-[#64748B]/5"
                            >
                              <Archive className="w-4 h-4 mr-1" /> Archive
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create / Edit modal */}
        <Dialog open={modalOpen} onOpenChange={(v) => (v ? setModalOpen(true) : closeModal())}>
          <DialogContent className="sm:max-w-[480px]" data-testid="gap-modal">
            <DialogHeader>
              <DialogTitle className="font-['Montserrat']">
                {editingGap ? 'Edit Gap' : 'Add Gap'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label htmlFor="gap-name" className="text-sm text-[#0F172A]">
                  Name <span className="text-[#D62B27]">*</span>
                </Label>
                <Input
                  id="gap-name"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Slow order taking"
                  className="mt-1"
                  data-testid="gap-form-name"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="gap-category" className="text-sm text-[#0F172A]">
                  Category <span className="text-[#94A3B8] text-xs">(optional)</span>
                </Label>
                <Input
                  id="gap-category"
                  value={form.category}
                  onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                  placeholder="e.g. Service Quality"
                  className="mt-1"
                  data-testid="gap-form-category"
                />
              </div>
              <div>
                <Label htmlFor="gap-description" className="text-sm text-[#0F172A]">
                  Description <span className="text-[#94A3B8] text-xs">(optional)</span>
                </Label>
                <Textarea
                  id="gap-description"
                  value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="What does this gap cover?"
                  rows={4}
                  className="mt-1"
                  data-testid="gap-form-description"
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button
                variant="outline"
                onClick={closeModal}
                disabled={saving}
                data-testid="gap-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={saveGap}
                disabled={saving || !form.name.trim()}
                className="bg-[#1A3E6F] hover:bg-[#15325A] text-white"
                data-testid="gap-save-btn"
              >
                {saving ? 'Saving...' : editingGap ? 'Save Changes' : 'Add Gap'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Archive confirmation */}
        <AlertDialog
          open={!!archiveTarget}
          onOpenChange={(v) => { if (!v && !archiving) setArchiveTarget(null); }}
        >
          <AlertDialogContent data-testid="archive-gap-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Archive this gap?</AlertDialogTitle>
              <AlertDialogDescription>
                It will no longer appear in gap selections.
                {archiveTarget?.name && (
                  <span className="block mt-2 font-medium text-[#0F172A]">
                    {archiveTarget.name}
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={archiving} data-testid="archive-gap-cancel">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmArchive}
                disabled={archiving}
                className="bg-[#D62B27] hover:bg-[#B91C1C] text-white"
                data-testid="archive-gap-confirm"
              >
                {archiving ? 'Archiving...' : 'Archive'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
